import { useMemo, useState } from "react";

import type { SymbolSet, SymbolSetItem } from "../../../../api/types";
import { ShapeDrawing } from "../../../../ui";
import { Check, Select, TextInput } from "../fields";
import { kindWord, usedWords } from "./words";

interface Props {
  set: SymbolSet;
  selected: string | null;
  onSelect: (code: string) => void;
  /** The name of a kind of drawing for its code (the code stays the filter's value). */
  drawingName?: (code: string) => string;
}

const ALL = "";

/** The symbol library to browse: the loaded shapes and this discipline's own, narrowed by kind, kind of drawing,
 * category, discipline and words. Each tile is drawn from its data. */
export function SymbolBrowser({ set, selected, onSelect, drawingName = (code) => code }: Props) {
  const [kind, setKind] = useState<string>("symbol");
  const [drawing, setDrawing] = useState(ALL);
  const [category, setCategory] = useState(ALL);
  const [discipline, setDiscipline] = useState(ALL);
  const [words, setWords] = useState("");
  const [withdrawn, setWithdrawn] = useState(false);
  const drawings = useMemo(() => [...new Set(set.items.map((i) => i.kind_of_drawing).filter(Boolean))].sort(), [set.items]);
  const shown = set.items.filter((item) => {
    if (kind && item.kind !== kind) return false;
    if (drawing && item.kind_of_drawing !== drawing) return false;
    if (category && item.category !== category) return false;
    if (discipline && item.discipline !== discipline) return false;
    if (item.withdrawn && !withdrawn && item.code !== selected) return false;
    const wanted = words.toLowerCase().split(/\s+/).filter(Boolean);
    const text = `${item.code.replace(/_/g, " ")} ${item.title}`.toLowerCase();
    return wanted.every((w) => text.includes(w));
  });

  return (
    <div className="stack">
      <div className="symbol-filters" role="group" aria-label="Narrow the library">
        <Select
          label="Show"
          value={kind}
          options={[
            { value: "symbol", label: "Symbols" },
            { value: "line_style", label: "Line styles" },
            { value: ALL, label: "Both" },
          ]}
          onChange={setKind}
        />
        <Select label="Kind of drawing" value={drawing} placeholder="Any" options={drawings.map((d) => ({ value: d, label: drawingName(d) }))} onChange={setDrawing} />
        <Select label="Category" value={category} placeholder="Any" options={(set.categories ?? []).map((c) => ({ value: c, label: c.replace(/_/g, " ") }))} onChange={setCategory} />
        <Select label="Discipline" value={discipline} placeholder="Any" options={(set.disciplines ?? []).map((d) => ({ value: d, label: d }))} onChange={setDiscipline} />
        <TextInput label="Words" value={words} onChange={setWords} hint="In the code or the name." />
        {set.items.some((item) => item.withdrawn) && <Check label="Also show the withdrawn ones" checked={withdrawn} onChange={setWithdrawn} />}
      </div>
      <p className="muted" aria-live="polite">
        {shown.length === 1 ? "1 shape" : `${shown.length} shapes`}
      </p>
      {shown.length === 0 ? (
        <p className="muted">Nothing in the library fits. Change the filters, or upload a drawing below.</p>
      ) : (
        <ul className="symbol-grid">
          {shown.map((item) => (
            <Tile key={`${item.kind}:${item.code}`} item={item} selected={item.code === selected} onSelect={onSelect} drawingName={drawingName} />
          ))}
        </ul>
      )}
    </div>
  );
}

function Tile({ item, selected, onSelect, drawingName }: { item: SymbolSetItem; selected: boolean; onSelect: (code: string) => void; drawingName: (code: string) => string }) {
  const guessed = item.unsure_points?.length ?? 0;
  return (
    <li className="symbol-tile" data-selected={selected || undefined}>
      <button type="button" className="symbol-tile__button" aria-pressed={selected} onClick={() => onSelect(item.code)}>
        <ShapeDrawing shape={item.shape} kind={item.kind} label={`${item.kind === "line_style" ? "Line style" : "Symbol"} ${item.code}`} size={56} />
        <code>{item.code}</code>
        <span>{item.title}</span>
      </button>
      <span className="muted">
        {item.origin === "loaded" ? "Library" : "This discipline"}
        {item.changed ? ", changed here" : ""}, {kindWord(item.kind)}, {drawingName(item.kind_of_drawing)}
      </span>
      {item.withdrawn && <span className="muted">{item.origin === "loaded" ? "Proposed for withdrawal" : "Withdrawn"}{item.replaced_by ? `, replaced by ${item.replaced_by}` : ""}</span>}
      <span className="muted">{usedWords(item)}</span>
      {guessed > 0 && <span className="muted">{guessed === 1 ? "1 guessed point to confirm" : `${guessed} guessed points to confirm`}</span>}
    </li>
  );
}
